import { SelectField, SelectFieldProps } from '@portals/core/src/components/SelectField/SelectField';
import { SelectFieldItem } from '@portals/core/src/components/SelectFieldItem/SelectFieldItem';
import { Field, FieldProps } from 'formik';
import React, { ChangeEventHandler, FocusEventHandler, useCallback, useMemo } from 'react';

import { useSelectValidator } from '../../hooks/SelectValidator/SelectValidator';
import { teeEvent } from '../../utilties/teeEvent';

export interface Option {
  id: string;
  text?: string;
}
export type SelectInputProps = Omit<SelectFieldProps, 'name' | 'options' | 'value' | 'error'> & {
  name: string;
  placeholder?: SelectFieldProps['placeholder'];
  selectOptions: Option[];
  locked?: SelectFieldProps['locked'];
  loading?: boolean;
};

export const SelectInput: React.FC<SelectInputProps> = ({ name, selectOptions, loading, required, ...props }) => {
  const datalistId = `${props.id}-datalist`;
  const validate = useSelectValidator({ required: required ?? false });
  const datalistComponent = useMemo(
    () => (
      <datalist id={datalistId}>
        {selectOptions.map((option) => (
          <option key={option.id} value={option.text} />
        ))}
      </datalist>
    ),
    [datalistId, selectOptions]
  );

  const fieldComponent = useCallback(
    ({ field: { value, ...field }, form, locked, ...props }: SelectInputProps & FieldProps) => {
      const error = form.errors[name] as string;
      const hasError = form.touched[name] && Boolean(error);
      const helperText = hasError ? error : props.helperText;

      const onBlur = teeEvent(props.onBlur as FocusEventHandler<HTMLInputElement>, field.onBlur);
      const onChange = teeEvent(props.onChange as ChangeEventHandler<HTMLInputElement>, field.onChange);

      return (
        <SelectField
          multiple={false}
          locked={locked || loading}
          error={hasError}
          helperText={helperText}
          {...field}
          value={value || ''}
          {...props}
          onBlur={onBlur}
          onChange={onChange}
        >
          {selectOptions.map((option) => (
            <SelectFieldItem key={option.id} id={option.id} value={option.id}>
              {option.text}
            </SelectFieldItem>
          ))}
        </SelectField>
      );
    },
    [selectOptions, loading, name]
  );
  return (
    <Field
      component={fieldComponent}
      name={name}
      datalist={datalistComponent}
      required={required}
      validate={validate}
      {...props}
      inputProps={{ list: datalistId, required, ...props.inputProps }}
    />
  );
};
